import Tick from '@atlaskit/icon/core/status-success';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { getCloudId, useDatabaseContent } from '@databases/database-state';
import { useDialog } from '@databases/dialog';
import { useFlags } from '@databases/flags';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { DatabaseChangeType } from '@databases/types/redux';
import { getValueId, generateId } from '@databases/utils/utils';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';

import { currentHostFrameContext } from '@canvas/canvas-state/host-api';
import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { type CommentThreadEvent } from '@canvas/comments/comment/store';
import { CommentContentType } from '@canvas/comments/comment-types';
import { CommentThreadDialogLazy } from '@canvas/comments/new-comment-components/thread/dialog';
import { type CommentThreadDialogLazyProps } from '@canvas/comments/new-comment-components/thread/dialog';
import { type ThreadNavigationOpts } from '@canvas/comments/new-comment-components/thread/types';
import { getCommentStore } from '@canvas/comments/state/comment-store';
import { getNotificationStore } from '@canvas/comments/state/notification-store';
import { getReactionsStore } from '@canvas/comments/state/reactions-store';
import { type CommentBaseProps } from '@canvas/comments/thread/types';
import { useThread } from '@canvas/comments/thread/use-thread';

import { useCommentActionsContext } from '../hooks/useCommentContext';
import { useDeleteComment } from '../hooks/useDeleteComment';
import { messages } from '../i18n';
import { type ThreadWithValueId } from '../type';

type CommonDialogProps = Omit<CommentBaseProps, 'onCancelled'> & {
  threadId: string;
  verticalPadding: number;
  showCloseButton?: boolean;
  showChildrenComments?: boolean;
  showProfileCard?: boolean;
  cloudId: string | undefined;
  contentId: string;
  accountId: string | undefined;
  activationId?: string;
  threadNavigationOpts?: ThreadNavigationOpts;
  contentType?: CommentContentType;
  originalSelection?: string;
};
interface CommentThreadProps {
  contentId: string;
  entryId: string;
  fieldId: string;
  commentToggled: boolean;
  thread: ThreadWithValueId;
  currentUserId: string | undefined;
  isCreate: boolean;
  threadNavigationOpts?: ThreadNavigationOpts;
  stringRepresentation?: string;
  setIsDeleting: (isDeleting: boolean) => void;
  onThreadCreate: (evt: CommentThreadEvent) => void;
  handleClose: () => void;
}

/**
 * NewCommentThread component for the Consolidated Comments project:
 * See: https://hello.atlassian.net/wiki/spaces/Comments/pages/5061707298
 * Same as the old CommentThread, but instead call the new thread components instead
 */
const CommentThread: React.FC<CommentThreadProps> = ({
  contentId,
  entryId,
  fieldId,
  commentToggled,
  thread,
  currentUserId,
  isCreate,
  threadNavigationOpts,
  stringRepresentation,
  setIsDeleting,
  onThreadCreate,
  handleClose,
}) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const store = getCommentStore(undefined, currentHostFrameContext);
  const notificationStore = getNotificationStore();
  const reactionsStore = getReactionsStore();
  const cloudId = getCloudId();
  const client = useAnalytics();
  const { setOpenThread } = useCommentActionsContext();
  const { showDialog } = useDialog();
  const { formatMessage } = useIntl();
  const { showFlag, addFlag, clearFlags, hideFlag } = useFlags();
  const [hasLocalChange, setHasLocalChange] = useState(false);
  const { deleteComment } = useDeleteComment();
  const { parentContentId, activationId } = useDatabaseContent();

  const reactionsContentId = parentContentId ?? contentId;

  const comments = useThread(
    store,
    thread?.threadId as string,
    thread?.threadId,
    [],
    cloudId,
    reactionsContentId,
    CommentContentType.DATABASE,
    activationId
  );

  const onThreadUpdate = useCallback(() => {
    if (thread && thread.threadId) {
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.INCREMENT_VALUE_COMMENT_THREAD_VERSION_NUMBER,
          fieldId,
          entryId,
          threadId: thread.threadId,
        })
      );
    }
  }, [contentId, dispatch, entryId, fieldId, thread]);

  useEffect(() => {
    if (thread && thread.threadId && !hasLocalChange) {
      store.reloadThread(thread.threadId, thread.threadId);
    }

    // close dialog if thread was resolved or deleted remotely
    if (thread?.isResolved || (!thread?.threadId && comments.length > 0)) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread?.versionNumber]);

  useLayoutEffect(() => {
    if (!store) {
      return;
    }

    const subscriber = (evt: CommentThreadEvent) => {
      if (evt.type === 'threadCreated') {
        onThreadCreate(evt);
      } else if (
        evt.type === 'deletedReplyFromThread' ||
        evt.type === 'addedReplyToThread' ||
        evt.type === 'rootCommentChanged' // thread was resolved
      ) {
        onThreadUpdate();
      } else if (evt.type === 'threadChangedLocally') {
        setHasLocalChange(true);
      }
    };

    if (!commentToggled) {
      store.unsubscribe(subscriber);
      return;
    }

    store.subscribe(subscriber);

    return () => store.unsubscribe(subscriber);
  }, [
    store,
    contentId,
    reactionsContentId,
    entryId,
    fieldId,
    dispatch,
    commentToggled,
    thread?.threadId,
    thread?.isResolved,
    thread,
    onThreadUpdate,
    setOpenThread,
    onThreadCreate,
    cloudId,
    comments.length,
    handleClose,
    thread?.versionNumber,
  ]);

  const handleDelete = useCallback(
    (externalId: string) => {
      if (!thread?.threadId) {
        return;
      }

      setIsDeleting(true);
      showDialog('ConfirmDialog', {
        heading: formatMessage(
          messages['comments-confirm-dialog.delete-comment-heading']
        ),
        body: (
          <>{formatMessage(messages['comments-confirm-dialog.delete-comment-body'])}</>
        ),
        yesLabel: formatMessage(
          messages['comments-confirm-dialog.delete-comment-confirm-button']
        ),
        noLabel: formatMessage(
          messages['comments-confirm-dialog.delete-comment-cancel-button']
        ),
        appearance: 'warning',
        onClose: async (result) => {
          if (result) {
            await store.deleteComment(thread.threadId, externalId);
            deleteComment({
              contentId,
              entryId,
              fieldId,
              threadId: thread.threadId,
              externalId,
              commentCount: thread.commentCount,
              handleClose,
            });
          }
          setIsDeleting(false);
        },
      });
    },
    [
      thread?.threadId,
      thread?.commentCount,
      setIsDeleting,
      showDialog,
      formatMessage,
      store,
      deleteComment,
      contentId,
      entryId,
      fieldId,
      handleClose,
    ]
  );

  const handleResolve = () => {
    if (thread) {
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_VALUE_COMMENT_THREAD_RESOLVED,
          entryId,
          fieldId,
          threadId: thread.threadId,
          isResolved: true,
        })
      );
      onThreadUpdate();

      const flagMessage = formatMessage(
        messages['comments-snackbar.confirm-resolved-comment']
      );
      const flagId = `comment-resolved-${thread.threadId}`;
      const undoAction = {
        content: formatMessage(messages['comments-snackbar.undo-resolved-comment']),
        onClick: () => {
          store.reOpenComment(thread.threadId, thread.threadId);
          dispatch(
            addChangeToDraft(contentId, {
              type: DatabaseChangeType.SET_VALUE_COMMENT_THREAD_RESOLVED,
              entryId,
              fieldId,
              threadId: thread.threadId,
              isResolved: false,
            })
          );
          setOpenThread({
            valueId: getValueId(entryId, fieldId),
            threadId: thread.threadId,
          });
          if (isFeatureFlagEnabled('database-frontend-flags-over-bridge')) {
            hideFlag(flagId);
          } else {
            clearFlags();
          }
        },
      };

      if (isFeatureFlagEnabled('database-frontend-flags-over-bridge')) {
        showFlag({
          id: flagId,
          title: flagMessage,
          type: 'success-circle',
          close: 'auto',
          actions: [undoAction],
        });
      } else {
        addFlag({
          title: flagMessage,
          description: '',
          appearance: 'normal',
          icon: () => (
            <Tick spacing="spacious" label="" color={token('color.icon.success')} />
          ),
          actions: [undoAction],
        });
      }
      handleClose();
    }
  };

  const onReply = useCallback(
    (externalId?: string, parentContentId?: string) => {
      if (!thread) {
        return;
      }

      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.INCREMENT_VALUE_COMMENT_THREAD_COMMENT_COUNT,
          entryId,
          fieldId,
          threadId: thread.threadId,
        })
      );

      if (externalId && parentContentId) {
        client.sendDatabaseCommentCreatedEvent({
          contentId,
          databaseContainer: getDatabaseContainerType(),
          commentId: externalId,
          parentCommentContentId: parentContentId,
        });

        if (isFeatureFlagEnabled('unreadComments')) {
          notificationStore.markCommentsAsViewed([externalId]);
        }
      }

      onThreadUpdate();
    },
    [
      client,
      contentId,
      dispatch,
      entryId,
      fieldId,
      onThreadUpdate,
      thread,
      notificationStore,
    ]
  );

  const onClose = useCallback(() => {
    handleClose();

    if (isFeatureFlagEnabled('unreadComments')) {
      notificationStore.addCommentReferenceIdsAsReadToStore(
        comments
          .map((comment) => comment.id)
          .filter((commentId) => commentId !== undefined)
      );
    }
  }, [comments, notificationStore, handleClose]);

  const commonDialogProps: CommonDialogProps = {
    onDelete: handleDelete,
    onResolve: handleResolve,
    onReOpen: () => () => {},
    onReply: onReply,
    onClick: () => () => {},
    onEdit: () => () => {},
    onEditCancel: () => () => {},
    onCreated: (_: string, commentContentId?: string) => {
      if (commentContentId) {
        client.sendDatabaseCommentCreatedEvent({
          contentId,
          databaseContainer: getDatabaseContainerType(),
          commentId: commentContentId,
        });

        if (isFeatureFlagEnabled('unreadComments')) {
          notificationStore.markCommentsAsViewed([commentContentId]);
        }
      }
    },
    canCreate: true,
    store,
    reactionsStore,
    threadId: thread?.threadId ?? generateId(),
    comments,
    showCloseButton: true,
    showChildrenComments: true,
    showProfileCard: true,
    verticalPadding: 0,
    cloudId,
    contentId: reactionsContentId,
    accountId: currentUserId,
    activationId,
    contentType: CommentContentType.DATABASE,
    originalSelection: stringRepresentation,
    threadNavigationOpts: threadNavigationOpts && threadNavigationOpts,
  };

  const properties: CommentThreadDialogLazyProps = !thread?.threadId
    ? {
        type: 'unpersisted',
        onClose: onClose,
        onCancelled: () => {},
        ...commonDialogProps,
        notificationStore,
      }
    : {
        type: 'persisted',
        externalId: thread.threadId,
        onClose: onClose,
        onCancelled: () => {},
        ...commonDialogProps,
        notificationStore,
      };

  // If a comment should have content but does not yet, we render nothing. This
  // will avoid UI jank in the interim. Can be used in future for a loading state.
  if (!thread?.threadId && comments.length === 0 && !isCreate) {
    return null;
  }

  return (
    <Suspense fallback={<Spinner interactionName="load" />}>
      <CommentThreadDialogLazy {...properties} />
    </Suspense>
  );
};

export default CommentThread;
